<template>
  <el-aside
    class="menu"
    :width="null"
  >
    <el-menu
      class="menu__nav el-menu-side-nav"
      :class="{ 'menu__nav--collapsed': collapsed }"
      :key="`menu__nav--${manageName}`"
      ref="menu"
      router
      :default-active="defaultActiveItem"
      :collapse-transition="false"
      :style="{'--menu-back-height': canGoBack?'5rem':'0rem'}"
    >
      <h1
        class="menu__title"
        :class="{ 'menu__title--superadmin': isSuperAdmin }"
      >
        <img
          v-if="!collapsed || isSuperAdmin"
          class="menu__logo"
          :src="`/images/logo${collapsed ? '-small' : ''}.svg`"
          alt="OWQLO"
          @click="onLogoClick"
        />
        <i
          class="menu__collapse"
          :class="{
            'menu__collapse--collapsed': collapsed,
            'icon-menu': collapsed,
            'icon-arrow-line-left': !collapsed,
          }"
          @click="toggleMenu"
        />
      </h1>

      <div
        class="menu__manage"
        :class="{ 'menu__manage--collapsed': collapsed }"
        v-if="!isSuperAdmin"
      > 
        <div
          class="menu__manage__image"
          :alt="manageName"
          :style="{ backgroundImage: manageLogoUrl }"
        />
        <!-- Select -->
        <app-select 
          class="menu__manage__name"
          :key="currentManage.model.id"
          :option="{
            id: currentManage.model.id,
            name: currentManage.model.name,
          }" 
          :options="selectList"
          :logo-url="manageLogoUrl" 
          :on-change-fn="doChangeSelect"
        />        
      </div>
      
      <template v-for="(menu, i) in menuList">
        <el-menu-item
          v-if="menu.type === 'item'"
          class="menu__item"
          :key="`menu__item--${i}`"
          :index="menu.index"
          :route="menu.route"
          :disabled="menu.disabled"
        >
          <div class="menu__item__button">
            <i
              class="menu__item__icon"
              :class="menu.icon"
            />
            <app-i18n
              class="menu__item__label"
              slot="title"
              :code="menu.codeLanguage"
            />
          </div>
        </el-menu-item>
        <app-i18n
          v-else-if="menu.type === 'category'"
          class="menu__category"
          :class="{ 'menu__category--collapsed': collapsed }"
          :key="`menu__category--${i}`"
          :code="menu.codeLanguage"
        />
      </template>
    </el-menu>
    <div
      v-if="canGoBack"
      class="menu__back"
      :class="{ 'menu__back--collapsed': collapsed }"
      @click="doBackManage"
    >
      <i class="icon-arrow-line-left" />
      <app-i18n
        slot="title"
        :code="backButtonI18nCode"
      />
    </div>
  </el-aside>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { isEqual, isEmpty } from 'lodash';

export default {
  name: 'app-menu',

  data() {
    return {
      defaultActiveItem: null
    };
  },

  computed: {
    ...mapGetters({
      collapsed: 'layout/menuCollapsed',
      isMobile: 'layout/isMobile',
      currentUser: 'auth/currentUser',
      maxManage: 'auth/manage/maxManage',
      canGoBack: 'auth/manage/canGoBack',
      currentManage: 'auth/manage/currentManage',
      isMonoSubproperty: 'auth/manage/isMonoSubproperty',
      menuList: 'layout/menu/list',
      menuStyle: 'layout/menu/style',
      selectList: 'layout/menu/selectList',
      propertySteps: 'property/requirements/steps',
      subpropertySteps: 'subproperty/requirements/steps',
    }),

    manageName() {
      if(!this.currentManage) return '';
      if (
        (this.currentManage.type === 'property' || this.currentManage.type === 'subproperty') &&
        this.currentManage.property &&
        this.currentManage.property.name
      ) {
        return this.currentManage.property.name;
      }
      return '';
    },

    manageSubName() {
      if(!this.currentManage) return '';
      if (
        this.currentManage.type === 'subproperty' &&
        this.currentManage.model &&
        this.currentManage.model.name
      ) {
        return this.currentManage.model.name;
      }
      return '';
    },

    manageLogoUrl() {
      if(!this.currentManage) return null;

      if (
        (this.currentManage.type === 'property' ) &&
        this.currentManage.property &&
        this.currentManage.property.logos &&
        this.currentManage.property.logos.length > 0
      ) {
        return `url("${this.currentManage.property.logos[0].publicUrl}")`;

      } else if ( this.currentManage.type === 'subproperty' &&
                  this.currentManage.subproperty &&
                  this.currentManage.subproperty.logo &&
                  this.currentManage.subproperty.logo.length > 0
                ) {
        return `url("${this.currentManage.subproperty.logo[0].publicUrl}")`;
      }
      
      return null;
    },

    backButtonI18nCode() {
      const { type } = this.currentManage;
      if (type === 'subproperty' && this.isMonoSubproperty) { return 'common.back.admin'}
      if (type === 'subproperty') { return 'common.back.property'}
      if (type === 'property') return 'common.back.admin';
      return 'common.back.default';
    },

    isSuperAdmin() {
      return (
        this.manageName.trim().length === 0 &&
        this.manageSubName.trim().length === 0
      );
    },

    mainPathIndex() {
      let { path } = this.$route;
      let index, menuItem;

      while (!menuItem) {
        index = path === '/' ? '' : path;
        menuItem = this.findMenuItemByIndex(index);
        if (!menuItem) {
          const pathParts = path.split('/');
          pathParts.pop();
          if (pathParts.length <= 1) menuItem = {};
          path = pathParts.join('/');
        }
      }

      return path;
    }
  },

  created() {
    this.defaultActiveItem = this.mainPathIndex;
    this.onMenuSelect(this.defaultActiveItem);
    this.fetchRequirementSteps();
  },

  methods: {
    ...mapActions({
      toggleMenu: 'layout/toggleMenu',
      setCurrentMenuItem: 'layout/menu/setCurrentMenuItem',
      doBackManage: 'auth/manage/doBackManage',
      doInitMenu: 'layout/menu/doInitMenu',
      doChangeSelect: 'layout/menu/doChangeSelect',
      doFetchPropertyRequirements: 'property/requirements/doFetch',
      doFetchSubpropertyRequirements: 'subproperty/requirements/doFetch',
      doResetPropertyRequirements: 'property/requirements/doReset',
      doResetSubpropertyRequirements: 'subproperty/requirements/doReset',
    }),

    async fetchRequirementSteps() {
      const manageType = this.currentManage.type;

      this.doResetPropertyRequirements();
      this.doResetSubpropertyRequirements();

      switch (manageType) {
        case 'property':
          await this.doFetchPropertyRequirements({
            propertyId: this.currentManage.property.id,
            userId: this.currentUser.id,
          });
          break;

        case 'subproperty':
          await this.doFetchSubpropertyRequirements({
            propertyId: this.currentManage.property.id,
            subpropertyId: this.currentManage.subproperty.id,
            userId: this.currentUser.id,
          });
          break;
      }
    },

    findMenuItemByIndex(index) {
      return this.menuList.find(menu => menu.index === index);
    },

    onMenuSelect(index) {
      const item = this.findMenuItemByIndex(index);
      this.setCurrentMenuItem(item);
    },

    onLogoClick() {
      this.$router.push('/');
    },

    initMenu(val, oldVal) {
      if (isEqual(val, oldVal)) return;
      if (isEmpty(val)) return;
      const manageType = this.currentManage.type;
      this.doInitMenu({ manageType, doNavigation: false });
    },
  },

  watch: {
    '$route'() {
      this.onMenuSelect(this.mainPathIndex);
      this.$refs.menu.activeIndex = this.mainPathIndex;
    },
    propertySteps: {
      deep: true,
      handler: 'initMenu',
    },
    subpropertySteps: {
      deep: true,
      handler: 'initMenu',
    },
    'currentManage.type': 'fetchRequirementSteps'
  }
};
</script>

<style scoped>
.menu {
  --menu-back-height: 5rem;

  position: relative;
  height: 100vh;
  max-height: 100vh;
  overflow: hidden;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
  z-index: 2000;
}

.menu__nav {
  width: 260px;
  height: calc(100vh - var(--menu-back-height));
  max-height: calc(100vh - var(--menu-back-height));
  overflow-y: auto;
  overflow-x: hidden;
}

.menu__nav--collapsed {
  width: 75px;
}

.menu__title {
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: auto 22px;
  grid-template-areas: 'logo icon';
  align-items: center;

  height: 80px;
  padding: 27px 13px;
  margin: 0;
  box-sizing: border-box;
}

.menu__title--superadmin {
  margin: 0 0 30px;
}

.menu__nav--collapsed .menu__title {
  justify-items: center;
  grid-auto-flow: column;
  grid-template-columns: 1fr;
  grid-template-areas: 'icon';
}

.menu__nav--collapsed .menu__title--superadmin {
  justify-items: center;
  grid-auto-flow: row;
  grid-template-columns: 1fr;
  grid-template-areas: 'icon' 'logo';
  grid-gap: 50px;
  height: auto;
  margin: 0;
}

.menu__logo {
  grid-area: logo;
  width: auto;
  height: 27px;
  cursor: pointer;
}

.menu__collapse {
  grid-area: icon;
  cursor: pointer;
  opacity: 0.8;
  font-size: 14px;
}

.menu__collapse--collapsed {
  font-size: 28px;
}

.menu__collapse:hover {
  opacity: 1;
}

.menu__manage {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  grid-gap: 12px;
  padding: 10px 0;
  margin: 0 13px;
  border-bottom: 1px solid rgba(204, 204, 204, .6);
  height: 80px;
  box-sizing: border-box;
}

.menu__manage--collapsed {
  grid-template-columns: 1fr;
  justify-items: center;
  border-bottom: 0;
  margin-bottom: 30px;
}

.menu__manage__name {
  font-size: 14px;
  line-height: 140%;
  color: #404040;
}

.menu__manage--collapsed .menu__manage__name {
  display: none;
}

.menu__manage__image {
  width: 44px;
  height: 44px;
  border-radius: 100%;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center center;
  box-shadow: 0 3px 10px 0 rgba(0, 0, 0, 0.1);
  background-color: #f1f0f0;
  box-sizing: border-box;
}

.menu__category {
  font-size: 12px;
  font-weight: 500;
  color: #808080;
  text-transform: uppercase;
  margin: 20px 20px 10px;
  cursor: default;
  display: block;
}

.menu__category--collapsed {
  opacity: 0;
}

.menu__category--collapsed:first-of-type {
  margin: 0 auto;
}

.menu__item {
  height: 50px;
  line-height: initial;
  border: 0;
  background-color: #ffffff;
  margin-bottom: 10px;
  padding: 0 13px !important;
}

.menu__item:last-of-type {
  margin-bottom: 0;
}

.menu__item:hover,
.menu__item.is-active {
  background-color: #ffffff;
}

.menu__item__button {
  border-radius: 25px;
  height: 100%;
  display: grid;
  grid-template-columns: 28px 1fr;
  align-items: center;
  padding: 0 20px;
  grid-gap: 10px;
}

.menu__item.is-active .menu__item__button {
  background-color: #f1f0f0;
  transition: background-color .4s linear;
}

.menu__nav--collapsed .menu__item__button {
  padding: 0;
  grid-template-columns: 1fr 0;
  grid-gap: 0;
  justify-items: center;
}

.menu__item__icon {
  opacity: 0.8;
  font-size: 20px;
  text-align: center;
  transition: opacity .2s linear;
  color: #404040;
}

.menu__item:hover .menu__item__button i.menu__item__icon,
.menu__item.is-active .menu__item__button i.menu__item__icon {
  opacity: 1;
  color: #404040;
}

.menu__item__label {
  opacity: 0.8;
  font-size: 14px;
  font-weight: bold;
  color: #404040;
  transition: opacity .2s linear;
}

.menu__item:hover .menu__item__label,
.menu__item.is-active .menu__item__label {
  opacity: 1;
}

.menu__nav--collapsed .menu__item__label,
.menu__nav--collapsed .menu__item:hover .menu__item__label,
.menu__nav--collapsed .menu__item.is-active .menu__item__label {
  opacity: 0;
  transition: none;
}

.menu__back,
.menu__back:hover,
.menu__back.is-active {
  position: absolute;
  height: var(--menu-back-height);
  padding: 0 30px !important;
  left: 0px;
  bottom: 0px;
  width: 100%;
  background-color: #f8f8f8;
  font-size: 14px;
  font-weight: bold;
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: auto 1fr;
  grid-gap: 15px;
  align-items: center;
  cursor: pointer;
  box-sizing: border-box;
}

.menu__back span,
.menu__back i {
  opacity: 0.8;
  color: #404040;
}

.menu__back:hover span,
.menu__back:hover i {
  opacity: 1;
  color: #404040;
}

.menu__back--collapsed span,
.menu__back--collapsed:hover span {
  opacity: 0;
}
</style>
